import React from 'react';
import { downloadRunFile } from '@services/fmdc';
import { saveBlobAsFile } from '@utils/documentVault';

export const DOWNLOAD_STATUS = {
  INACTIVE: 'inactive',
  RUNNING: 'running',
  COMPLETED: 'completed',
  ERROR: 'error',
};

// The workbook is rendered on demand and can take tens of seconds, so a slow download is normal
// rather than broken. The elapsed counter exists to say so, and starts reporting only once the
// wait stops feeling instant — the same shape as hooks/features/useDocumentDownload.
const ELAPSED_HINT_DELAY_MS = 10000;
const ELAPSED_TICK_MS = 1000;

export const useRunDownload = (runId) => {
  const [state, setState] = React.useState({ status: DOWNLOAD_STATUS.INACTIVE, kind: null, error: null });
  const [elapsedMs, setElapsedMs] = React.useState(0);
  const abortRef = React.useRef(null);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Leaving the page mid-download abandons the request: there is nothing to reattach to.
      abortRef.current?.abort();
    };
  }, []);

  const isRunning = state.status === DOWNLOAD_STATUS.RUNNING;

  React.useEffect(() => {
    if (!isRunning) {
      setElapsedMs(0);
      return undefined;
    }

    const startedAt = Date.now();
    let interval = null;

    // Nothing displays the counter until the hint appears, so don't re-render once a second for
    // the whole wait — most downloads finish inside the delay.
    const timeout = setTimeout(() => {
      setElapsedMs(Date.now() - startedAt);
      interval = setInterval(() => {
        setElapsedMs(Date.now() - startedAt);
      }, ELAPSED_TICK_MS);
    }, ELAPSED_HINT_DELAY_MS);

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [isRunning]);

  const download = React.useCallback(
    async (kind) => {
      // One at a time: a second click on either kind would only race the first to the same file.
      if (abortRef.current) return null;

      const controller = new AbortController();
      abortRef.current = controller;
      setState({ status: DOWNLOAD_STATUS.RUNNING, kind, error: null });

      const result = await downloadRunFile(runId, kind, { signal: controller.signal });

      abortRef.current = null;
      if (!isMountedRef.current) return result;

      if (result.canceled) {
        setState({ status: DOWNLOAD_STATUS.INACTIVE, kind: null, error: null });
        return result;
      }

      if (!result.ok) {
        setState({ status: DOWNLOAD_STATUS.ERROR, kind, error: result.error, statusCode: result.status });
        return result;
      }

      saveBlobAsFile(result.blob, result.filename);
      setState({ status: DOWNLOAD_STATUS.COMPLETED, kind, error: null });
      return result;
    },
    [runId]
  );

  const reset = React.useCallback(() => {
    setState({ status: DOWNLOAD_STATUS.INACTIVE, kind: null, error: null });
  }, []);

  return {
    status: state.status,
    kind: state.kind,
    error: state.error,
    statusCode: state.statusCode,
    isRunning,
    showElapsedHint: isRunning && elapsedMs >= ELAPSED_HINT_DELAY_MS,
    elapsedSeconds: Math.floor(elapsedMs / 1000),
    download,
    reset,
  };
};
